/**
 * RegionPolygonsLayer — Phase 2
 *
 * Renders Saudi Arabia's 13 regions on MapLibre using the same
 * visual identity as the current D3 SVG implementation:
 * - Region fill colors from REGION_COLORS palette
 * - Subtle border glow
 * - Hover highlight
 * - Inner glow simulation
 * - Region labels with collision detection
 * - Progressive fade at deep zoom
 * - Capital city network nodes & connections
 *
 * Data source: saudi_regions_simplified.json (same file, no copies)
 */
import { useEffect, useRef, useCallback } from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import saudiGeoData from '../../../data/saudi_regions_simplified.json';
import { ZOOM, CAMERA } from '../interactions/constants';

/* ── Region color palette ── */
const REGION_COLORS = {
  riyadh:   { fill: '#24916F', glow: '#2FAE8F' },
  makkah:   { fill: '#234A74', glow: '#4A8AC0' },
  madinah:  { fill: '#D6A84F', glow: '#E8C77B' },
  eastern:  { fill: '#144D4E', glow: '#2A8C8E' },
  qassim:   { fill: '#38B99A', glow: '#4FCBB0' },
  asir:     { fill: '#8B5A2B', glow: '#C09060' },
  tabuk:    { fill: '#5A7B51', glow: '#8EBE81' },
  hail:     { fill: '#6C5E7D', glow: '#9D8EAD' },
  northern: { fill: '#4C6A90', glow: '#7D9AC0' },
  jazan:    { fill: '#8A5429', glow: '#BA8459' },
  najran:   { fill: '#7B5433', glow: '#AB8463' },
  bahah:    { fill: '#5A8E69', glow: '#8ABC99' },
  jawf:     { fill: '#4A6F6F', glow: '#7A9F9F' },
};
const DEFAULT_COLOR = { fill: '#24916F', glow: '#2FAE8F' };

/* ── Capital nodes for network visualization ── */
const CAPITAL_NODES = [
  { id: 'riyadh',   lng: 46.7167, lat: 24.6333, intensity: 1.0,  hub: true },
  { id: 'jeddah',   lng: 39.1728, lat: 21.5433, intensity: 0.85, hub: true },
  { id: 'makkah',   lng: 39.8261, lat: 21.4225, intensity: 0.95, hub: true },
  { id: 'madinah',  lng: 39.6142, lat: 24.4672, intensity: 0.9,  hub: true },
  { id: 'dammam',   lng: 50.1033, lat: 26.4367, intensity: 0.8,  hub: true },
  { id: 'buraydah', lng: 43.9750, lat: 26.3267, intensity: 0.6 },
  { id: 'abha',     lng: 42.5053, lat: 18.2164, intensity: 0.65 },
  { id: 'tabuk',    lng: 36.5714, lat: 28.3836, intensity: 0.55 },
  { id: 'hail',     lng: 41.6908, lat: 27.5114, intensity: 0.5 },
  { id: 'arar',     lng: 41.0236, lat: 30.9753, intensity: 0.45 },
  { id: 'jazan',    lng: 42.5611, lat: 16.8892, intensity: 0.5 },
  { id: 'najran',   lng: 44.1322, lat: 17.4917, intensity: 0.45 },
  { id: 'bahah',    lng: 41.4683, lat: 20.0000, intensity: 0.4 },
  { id: 'sakaka',   lng: 40.2064, lat: 29.9697, intensity: 0.45 },
  { id: 'khobar',   lng: 50.2083, lat: 26.2794, intensity: 0.7 },
  { id: 'taif',     lng: 40.4167, lat: 21.2703, intensity: 0.55 },
  { id: 'yanbu',    lng: 38.0631, lat: 24.0895, intensity: 0.45 },
  { id: 'jubail',   lng: 49.6225, lat: 27.0046, intensity: 0.5 },
];

const NETWORK_LINKS = [
  [0, 1], [0, 4], [0, 3], [0, 5],
  [2, 1], [2, 3], [1, 3],
  [3, 16], [4, 14], [4, 17],
  [5, 8], [8, 7], [7, 13],
  [8, 9], [2, 6], [6, 10],
  [6, 11], [15, 12], [3, 7],
];

/* ── Build MapLibre match expression for region fill colors ── */
function buildColorExpression(colorKey) {
  const stops = [];
  for (const [regionId, colors] of Object.entries(REGION_COLORS)) {
    stops.push(regionId, colors[colorKey]);
  }
  return ['match', ['get', 'id'], ...stops, DEFAULT_COLOR[colorKey]];
}

/* ── Build network lines GeoJSON ── */
function buildNetworkGeoJSON() {
  const features = NETWORK_LINKS.map(([fromIdx, toIdx], i) => {
    const from = CAPITAL_NODES[fromIdx];
    const to = CAPITAL_NODES[toIdx];
    if (!from || !to) return null;
    return {
      type: 'Feature',
      properties: { id: i },
      geometry: {
        type: 'LineString',
        coordinates: [[from.lng, from.lat], [to.lng, to.lat]],
      },
    };
  }).filter(Boolean);
  return { type: 'FeatureCollection', features };
}

/* ── Build capital nodes GeoJSON ── */
function buildNodesGeoJSON() {
  return {
    type: 'FeatureCollection',
    features: CAPITAL_NODES.map(node => ({
      type: 'Feature',
      properties: {
        id: node.id,
        intensity: node.intensity,
        hub: node.hub ? 1 : 0,
        radius: 3 + node.intensity * 5,
      },
      geometry: { type: 'Point', coordinates: [node.lng, node.lat] },
    })),
  };
}

/* ── Build region labels GeoJSON (centroids) ── */
function buildLabelsGeoJSON() {
  return {
    type: 'FeatureCollection',
    features: saudiGeoData.features.map(f => ({
      type: 'Feature',
      properties: {
        id: f.properties.id,
        name_ar: (f.properties.name_ar || '').replace(/^منطقة\s+/, '').replace(/^المنطقة\s+/, ''),
        name_en: f.properties.name_en?.replace(' Region', '') || '',
      },
      geometry: {
        type: 'Point',
        coordinates: [f.properties.centroid_lng, f.properties.centroid_lat],
      },
    })),
  };
}

/* ════════════════════════════════════════════════════════════
 * Component
 * ════════════════════════════════════════════════════════════ */
export default function RegionPolygonsLayer({ map, containerRef, onRegionClick, theme = 'dark', statsMode = false, onStatsRegionSelect }) {
  const { language } = useLanguage();
  const isArabic = language === 'ar';
  const hoveredRef = useRef(null);
  const addedRef = useRef(false);
  const statsModeRef = useRef(statsMode);
  const onStatsRegionSelectRef = useRef(onStatsRegionSelect);

  // Keep refs current
  useEffect(() => { statsModeRef.current = statsMode; }, [statsMode]);
  useEffect(() => { onStatsRegionSelectRef.current = onStatsRegionSelect; }, [onStatsRegionSelect]);
  const isLight = theme === 'light';

  /* ── Safe helpers — prevent "already exists" errors (strict mode / HMR) ── */
  const safeSource = useCallback((id, opts) => {
    if (map && !map.getSource(id)) map.addSource(id, opts);
  }, [map]);
  const safeLayer = useCallback((layer) => {
    if (map && !map.getLayer(layer.id)) map.addLayer(layer);
  }, [map]);

  /* ── Add sources & layers once ── */
  useEffect(() => {
    if (!map || addedRef.current) return;
    addedRef.current = true;

    /* ─── Saudi Focus Mask: dim everything outside Saudi Arabia ─── */
    const worldRing = [[-180, -85], [180, -85], [180, 85], [-180, 85], [-180, -85]];
    const saudiHoles = [];
    saudiGeoData.features.forEach(f => {
      const geom = f.geometry;
      if (geom.type === 'Polygon') {
        saudiHoles.push(geom.coordinates[0]);
      } else if (geom.type === 'MultiPolygon') {
        geom.coordinates.forEach(poly => saudiHoles.push(poly[0]));
      }
    });

    safeSource('saudi-mask', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [{
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'Polygon',
            coordinates: [worldRing, ...saudiHoles],
          },
        }],
      },
    });

    safeLayer({
      id: 'saudi-mask-fill',
      type: 'fill',
      source: 'saudi-mask',
      paint: {
        'fill-color': isLight ? '#EDE8DD' : '#040a14',
        'fill-opacity': 1,
      },
    });

    /* ═══ VISUAL: Cosmic Starfield with Constellations ═══ */
    const sfSize = 512;
    const sfCanvas = document.createElement('canvas');
    sfCanvas.width = sfSize;
    sfCanvas.height = sfSize;
    const sf = sfCanvas.getContext('2d');
    sf.clearRect(0, 0, sfSize, sfSize);
    const allStars = [];
    // Small scattered stars
    for (let i = 0; i < 220; i++) {
      const x = Math.random() * sfSize;
      const y = Math.random() * sfSize;
      const r = 0.3 + Math.random() * 1.5;
      const a = 0.15 + Math.random() * 0.65;
      sf.beginPath();
      sf.arc(x, y, r, 0, Math.PI * 2);
      sf.fillStyle = Math.random() > 0.7
        ? `rgba(150, 220, 230, ${a})`
        : `rgba(210, 225, 250, ${a})`;
      sf.fill();
      allStars.push({ x, y, r, a });
    }
    // Medium stars with soft glow halo
    for (let i = 0; i < 35; i++) {
      const x = Math.random() * sfSize;
      const y = Math.random() * sfSize;
      const r = 1.2 + Math.random() * 1.0;
      const a = 0.35 + Math.random() * 0.45;
      sf.beginPath();
      sf.arc(x, y, r * 3.5, 0, Math.PI * 2);
      sf.fillStyle = `rgba(170, 215, 245, ${a * 0.15})`;
      sf.fill();
      sf.beginPath();
      sf.arc(x, y, r, 0, Math.PI * 2);
      sf.fillStyle = `rgba(230, 240, 255, ${a})`;
      sf.fill();
      allStars.push({ x, y, r, a });
    }
    // Bright feature stars with layered glow
    for (let i = 0; i < 10; i++) {
      const x = Math.random() * sfSize;
      const y = Math.random() * sfSize;
      sf.beginPath();
      sf.arc(x, y, 8, 0, Math.PI * 2);
      sf.fillStyle = 'rgba(170, 220, 245, 0.05)';
      sf.fill();
      sf.beginPath();
      sf.arc(x, y, 4, 0, Math.PI * 2);
      sf.fillStyle = 'rgba(200, 230, 250, 0.1)';
      sf.fill();
      sf.beginPath();
      sf.arc(x, y, 1.5 + Math.random() * 0.8, 0, Math.PI * 2);
      sf.fillStyle = `rgba(240, 248, 255, ${0.55 + Math.random() * 0.4})`;
      sf.fill();
      allStars.push({ x, y, r: 2, a: 0.7 });
    }
    // Constellation lines — connect nearby bright stars with delicate lines
    const conStars = allStars.filter(s => s.r > 0.9 && s.a > 0.25);
    for (let i = 0; i < conStars.length; i++) {
      for (let j = i + 1; j < conStars.length; j++) {
        const dx = conStars[i].x - conStars[j].x;
        const dy = conStars[i].y - conStars[j].y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < 65 && dist > 18 && Math.random() > 0.6) {
          sf.beginPath();
          sf.moveTo(conStars[i].x, conStars[i].y);
          sf.lineTo(conStars[j].x, conStars[j].y);
          sf.strokeStyle = `rgba(140, 200, 220, ${0.03 + Math.random() * 0.06})`;
          sf.lineWidth = 0.25 + Math.random() * 0.25;
          sf.stroke();
        }
      }
    }
    if (!map.hasImage('starfield-pattern')) {
      map.addImage('starfield-pattern', sf.getImageData(0, 0, sfSize, sfSize));
    }
    safeSource('world-bg', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [{ type: 'Feature', properties: {},
          geometry: { type: 'Polygon', coordinates: [worldRing] } }],
      },
    });
    safeLayer({
      id: 'starfield-bg',
      type: 'fill',
      source: 'world-bg',
      paint: {
        'fill-pattern': 'starfield-pattern',
        'fill-opacity': ['interpolate', ['linear'], ['zoom'], 4, 1.0, 9, 0.55, 13, 0.1],
      },
    });

    /* ═══ VISUAL: Radial Focus Glow Behind Saudi Arabia ═══ */
    safeSource('saudi-glow-point', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [{ type: 'Feature', properties: {},
          geometry: { type: 'Point', coordinates: [44.55, 23.9] } }],
      },
    });
    safeLayer({
      id: 'saudi-radial-glow',
      type: 'circle',
      source: 'saudi-glow-point',
      paint: {
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 4, 220, 7, 350, 10, 550],
        'circle-color': 'rgba(25, 110, 100, 0.14)',
        'circle-blur': 1,
        'circle-opacity': ['interpolate', ['linear'], ['zoom'], 4, 0.55, 9, 0.2, 13, 0.03],
      },
    });

    // ─── Saudi interior solid fill (prevents gaps between regions) ───
    safeSource('saudi-interior', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: saudiHoles.map(ring => ({
          type: 'Feature',
          properties: {},
          geometry: { type: 'Polygon', coordinates: [ring] },
        })),
      },
    });

    /* ═══ VISUAL ENHANCEMENT 3: Depth Shadow ═══ */
    safeLayer({
      id: 'saudi-depth-shadow',
      type: 'line',
      source: 'saudi-interior',
      paint: {
        'line-color': 'rgba(0, 0, 0, 0.4)',
        'line-width': ['interpolate', ['linear'], ['zoom'], 4, 16, 9, 12, 13, 7],
        'line-blur': ['interpolate', ['linear'], ['zoom'], 4, 14, 9, 10, 13, 5],
        'line-translate': [4, 6],
        'line-translate-anchor': 'viewport',
        'line-opacity': ['interpolate', ['linear'], ['zoom'], 4, 0.35, 9, 0.2, 13, 0.06],
      },
    });

    safeLayer({
      id: 'saudi-interior-fill',
      type: 'fill',
      source: 'saudi-interior',
      paint: {
        'fill-color': isLight ? '#d5cfc3' : '#0a1520',
        'fill-opacity': 1,
      },
    });

    /* ═══ VISUAL: Saudi Border Glow (Premium Clean) ═══ */
    // 1) Glow layer (below) — soft wide teal glow on COUNTRY outline
    safeLayer({
      id: 'saudi-border-glow-outer',
      type: 'line',
      source: 'saudi-mask',
      paint: {
        'line-color': '#2dd4bf',
        'line-width': ['interpolate', ['linear'], ['zoom'], 4, 14, 9, 11, 13, 6],
        'line-blur': ['interpolate', ['linear'], ['zoom'], 4, 7, 9, 6, 13, 3.5],
        'line-opacity': ['interpolate', ['linear'], ['zoom'], 4, 0.54, 9, 0.34, 13, 0.10],
      },
    });
    // 2) Original border (above) — thin, sharp, brighter on COUNTRY outline
    safeLayer({
      id: 'saudi-border-glow-inner',
      type: 'line',
      source: 'saudi-mask',
      paint: {
        'line-color': '#2dd4bf',
        'line-width': ['interpolate', ['linear'], ['zoom'], 4, 1.8, 9, 1.2, 13, 0.6],
        'line-blur': 0.3,
        'line-opacity': ['interpolate', ['linear'], ['zoom'], 4, 0.90, 9, 0.60, 13, 0.22],
      },
    });

    // ─── Source: Region polygons ───
    safeSource('regions', {
      type: 'geojson',
      data: saudiGeoData,
    });

    /* ═══ Layers (bottom → top) — CLEAN V1 STYLE ═══ */

    // 1. Region fill — V1 palette, clean flat color
    safeLayer({
      id: 'regions-fill',
      type: 'fill',
      source: 'regions',
      paint: {
        'fill-color': buildColorExpression('fill'),
        'fill-opacity': ['interpolate', ['linear'], ['zoom'],
          ZOOM.REGION_MIN, isLight ? 0.55 : 0.94,
          ZOOM.CITY_MAX, isLight ? 0.25 : 0.5,
          11, isLight ? 0.08 : 0.2,
          ZOOM.PROJECT_MIN, 0,
        ],
      },
    });

    // 2. Dim overlay — hover dimming
    safeLayer({
      id: 'regions-dim',
      type: 'fill',
      source: 'regions',
      paint: {
        'fill-color': isLight ? 'rgba(237, 232, 221, 0.5)' : 'rgba(2, 5, 12, 0.55)',
        'fill-opacity': 0,
        'fill-opacity-transition': { duration: 300, delay: 0 },
      },
    });

    // 3. Region borders — clean thin lines (V1 style)
    safeLayer({
      id: 'regions-border',
      type: 'line',
      source: 'regions',
      paint: {
        'line-color': isLight ? 'rgba(80, 70, 55, 0.25)' : 'rgba(245, 241, 232, 0.22)',
        'line-width': ['interpolate', ['linear'], ['zoom'],
          ZOOM.REGION_MIN, 0.7,
          ZOOM.PROJECT_MIN, 0.3,
        ],
        'line-opacity': ['interpolate', ['linear'], ['zoom'],
          ZOOM.REGION_MIN, 1,
          ZOOM.DENSE_MAX, 0.5,
          ZOOM.PROJECT_MIN, 0,
        ],
      },
    });

    // 4. Hover highlight border
    safeLayer({
      id: 'regions-hover-border',
      type: 'line',
      source: 'regions',
      paint: {
        'line-color': 'rgba(255, 255, 255, 0.55)',
        'line-width': 1.8,
        'line-opacity': 0,
        'line-opacity-transition': { duration: 250, delay: 0 },
      },
      filter: ['==', ['get', 'id'], ''],
    });

    // 5. Hover fill
    safeLayer({
      id: 'regions-hover-fill',
      type: 'fill',
      source: 'regions',
      paint: {
        'fill-color': 'rgba(47, 174, 143, 0.1)',
        'fill-opacity': 0,
        'fill-opacity-transition': { duration: 250, delay: 0 },
      },
      filter: ['==', ['get', 'id'], ''],
    });

    // 6. Region labels
    const labelsData = buildLabelsGeoJSON();
    safeSource('region-labels', { type: 'geojson', data: labelsData });
    safeLayer({
      id: 'region-labels-text',
      type: 'symbol',
      source: 'region-labels',
      layout: {
        'text-field': ['get', 'name_ar'],
        'text-font': ['Noto Sans Bold'],
        'text-size': ['interpolate', ['linear'], ['zoom'],
          ZOOM.REGION_MIN, 13,
          ZOOM.CITY_MAX, 11,
          ZOOM.DENSE_MAX, 9,
        ],
        'text-allow-overlap': true,
        'text-ignore-placement': true,
        'text-anchor': 'center',
      },
      paint: {
        'text-color': '#FFFFFF',
        'text-halo-color': 'rgba(0, 0, 0, 0.5)',
        'text-halo-width': 1.2,
        'text-halo-blur': 0.5,
        'text-opacity': ['interpolate', ['linear'], ['zoom'],
          ZOOM.REGION_MIN, 0,
          7, 0,
          7.5, 0.65,
          ZOOM.CITY_MAX, 0.4,
          ZOOM.DENSE_MAX, 0.1,
          ZOOM.PROJECT_MIN, 0,
        ],
      },
    });

    /* ═══ Region click → smooth flyTo center of region ═══ */
    /* IMPORTANT: Check for cluster/project features FIRST.
       If a cluster or project exists at the click point, skip region zoom
       and let ProjectClusterLayer handle it via _v2handled flag. */
    map.on('click', 'regions-fill', (e) => {
      // Only handle in stats mode. Otherwise, DO NOT touch the event at all.
      if (!statsModeRef.current) return;
      if (e.originalEvent._v2handled) return;

      // Check if any cluster features exist at this point — clusters have priority
      const clusterCheckLayers = [
        'region-clusters-hitarea', 'region-clusters-circle',
        'city-clusters-hitarea', 'city-clusters-circle',
        'district-clusters-hitarea', 'district-clusters-circle',
        'project-clusters-hitarea', 'project-clusters',
        'project-points-hitarea', 'project-points',
        'featured-hitarea', 'featured-dot',
      ].filter(id => map.getLayer(id));
      if (clusterCheckLayers.length > 0) {
        const clusterHits = map.queryRenderedFeatures(e.point, { layers: clusterCheckLayers });
        if (clusterHits.length > 0) return; // Let cluster handler deal with it
      }

      e.originalEvent._v2handled = true;
      const feature = e.features?.[0];
      if (feature?.properties?.id && onStatsRegionSelectRef.current) {
        onStatsRegionSelectRef.current(feature.properties.id);
      }
    });

    /* ═══ Hover interaction — immersive dimming ═══ */
    /* PRIORITY: Clusters > Region hover. If mouse is over a cluster, suppress region hover. */
    map.on('mousemove', 'regions-fill', (e) => {
      // Check if any cluster/project features exist at this point
      const clusterCheckLayers = [
        'region-clusters-hitarea', 'region-clusters-circle',
        'city-clusters-hitarea', 'city-clusters-circle',
        'district-clusters-hitarea', 'district-clusters-circle',
        'project-clusters-hitarea', 'project-clusters',
        'project-points-hitarea', 'project-points',
        'featured-hitarea', 'featured-dot',
      ].filter(id => map.getLayer(id));

      if (clusterCheckLayers.length > 0) {
        const clusterHits = map.queryRenderedFeatures(e.point, { layers: clusterCheckLayers });
        if (clusterHits.length > 0) {
          // Cluster has priority — clear any active region hover
          if (hoveredRef.current) {
            hoveredRef.current = null;
            map.setPaintProperty('regions-dim', 'fill-opacity', 0);
            map.setFilter('regions-hover-border', ['==', ['get', 'id'], '']);
            map.setFilter('regions-hover-fill', ['==', ['get', 'id'], '']);
            map.setPaintProperty('regions-hover-border', 'line-opacity', 0);
            map.setPaintProperty('regions-hover-fill', 'fill-opacity', 0);
          }
          return; // Don't set cursor — let cluster hover handler do it
        }
      }

      const rid = e.features?.[0]?.properties?.id;
      if (!rid || rid === hoveredRef.current) return;

      hoveredRef.current = rid;

      // Dim all regions except hovered (smooth CSS transition)
      map.setFilter('regions-dim', ['!=', ['get', 'id'], rid]);
      map.setPaintProperty('regions-dim', 'fill-opacity', 1);

      // Spotlight hovered region
      map.setFilter('regions-hover-border', ['==', ['get', 'id'], rid]);
      map.setFilter('regions-hover-fill', ['==', ['get', 'id'], rid]);
      map.setPaintProperty('regions-hover-border', 'line-opacity', 1);
      map.setPaintProperty('regions-hover-fill', 'fill-opacity', 1);
      map.getCanvas().style.cursor = 'pointer';
    });

    map.on('mouseleave', 'regions-fill', () => {
      hoveredRef.current = null;

      // Remove dim (smooth transition out)
      map.setPaintProperty('regions-dim', 'fill-opacity', 0);

      // Remove spotlight
      map.setFilter('regions-hover-border', ['==', ['get', 'id'], '']);
      map.setFilter('regions-hover-fill', ['==', ['get', 'id'], '']);
      map.setPaintProperty('regions-hover-border', 'line-opacity', 0);
      map.setPaintProperty('regions-hover-fill', 'fill-opacity', 0);
      map.getCanvas().style.cursor = '';
    });

  }, [map]);

  /* ── Update label language when toggled ── */
  useEffect(() => {
    if (!map || !map.getSource('region-labels')) return;
    /* Update the symbol layer text based on current language */
    map.setLayoutProperty('region-labels-text', 'text-field', ['get', isArabic ? 'name_ar' : 'name_en']);
  }, [isArabic, map]);

  /* ── Subtle glow pulse on Saudi border (THROTTLED for touch performance) ── */
  useEffect(() => {
    if (!map) return;
    let animId;
    let start = null;
    let lastUpdate = 0;
    const THROTTLE_MS = 120; // ~8fps instead of 60fps — saves GPU on large touch screens
    const pulse = (ts) => {
      if (!start) start = ts;
      if (!map.getLayer('saudi-border-glow-outer')) { animId = requestAnimationFrame(pulse); return; }
      if (ts - lastUpdate < THROTTLE_MS) { animId = requestAnimationFrame(pulse); return; }
      lastUpdate = ts;
      const t = (ts - start) / 1000;
      // Sine wave: 0.42 → 0.62 → 0.42 over 2.5s
      const base = 0.50 + 0.10 * Math.sin(t * Math.PI * 2 / 2.5);
      try {
        map.setPaintProperty('saudi-border-glow-outer', 'line-opacity',
          ['interpolate', ['linear'], ['zoom'], 4, base, 9, base * 0.63, 13, base * 0.19]);
      } catch (_) {}
      animId = requestAnimationFrame(pulse);
    };
    animId = requestAnimationFrame(pulse);
    return () => cancelAnimationFrame(animId);
  }, [map]);

  /* ── Stats Mode: Region stat indicators (HTML markers on centroids) ── */
  const statsMarkersRef = useRef([]);

  useEffect(() => {
    if (!map) return;

    // Remove old markers
    statsMarkersRef.current.forEach(m => m.remove());
    statsMarkersRef.current = [];

    if (!statsMode) return;

    // Add a small indicator on each region centroid (offset below cluster)
    const maplibregl = require('maplibre-gl');
    saudiGeoData.features.forEach((f, idx) => {
      const { centroid_lng, centroid_lat, id } = f.properties;
      if (!centroid_lng || !centroid_lat) return;

      // Offset slightly south of centroid to avoid cluster overlap
      const offsetLat = centroid_lat - 0.35;

      const el = document.createElement('div');
      el.className = 'ss-region-marker';
      el.setAttribute('data-testid', `stats-region-marker-${id}`);
      el.style.animationDelay = `${idx * 0.04}s`;
      el.innerHTML = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>`;

      el.addEventListener('click', (e) => {
        e.stopPropagation();
        if (onStatsRegionSelectRef.current) {
          onStatsRegionSelectRef.current(id);
        }
      });

      const marker = new maplibregl.Marker({ element: el, anchor: 'center' })
        .setLngLat([centroid_lng, offsetLat])
        .addTo(map);

      statsMarkersRef.current.push(marker);
    });

    return () => {
      statsMarkersRef.current.forEach(m => m.remove());
      statsMarkersRef.current = [];
    };
  }, [map, statsMode]);

  return null;
}
